/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import FacilityForm from "components/forms/FacilityForm";
import Box from "components/generals/Box";
import Button from "components/generals/Button";
import ButtonDialog from "components/generals/ButtonDialog";
import DefaultCell from "components/generals/DefaultCell";
import { Modal } from "components/generals/Modal";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { CoreAPI_URLs } from "constants/http.const";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import useHttp from "hooks/http.hook";
import { get } from "http";
import { TableProps } from "interfaces";
import { FacilityInterface, HeaderEnum, UserRolesEnum } from "medicalsystem-shared";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectIsPatientOrDoctor } from "store/slices/auth.slice";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import { getNewFacilityDataContent } from "utils/model.utils";

/**
 * Facility table component for displaying and managing medical facility records.
 * 
 * Features:
 * - Displays facility information in sortable data table
 * - Columns: facility name, city, state, ZIP code, action buttons
 * - View/edit facility functionality via modal with FacilityForm
 * - Delete facility with confirmation dialog and list update
 * - Permission-based access control (read-only for patients/doctors)
 * - Automatic modal action type management (NEW/MODIFY/READ_ONLY)
 * - Sorted facility list for easy navigation
 * 
 * Facility Types:
 * - Hospitals
 * - Clinics
 * - Labs
 * - Imaging centers
 * - Pharmacies
 * - Surgery centers
 * - Medical offices
 * 
 * Security:
 * - Uses HeaderEnum.RESOURCE_ID for secure facility identification
 * - Permission-based rendering via PermissionWrapper
 * - Validates entity permissions before delete operations
 * - Read-only mode for patients and doctors
 * 
 * @param {TableProps<FacilityInterface>} props - Table properties
 * @param {FacilityInterface[]} props.modelList - Array of facility records to display
 * @param {Function} props.setModelList - Callback to update facility list after changes
 * 
 * @returns {JSX.Element} Facility data table with edit/delete actions
 */
const FacilityTable: React.FC<TableProps<FacilityInterface>> = ({
  modelList,
  setModelList,
  ...props
}) => {
  const [selectedFacility, setSelectedFacility] = useState<FacilityInterface>(
    getNewFacilityDataContent()
  );
  const [activeEditFacilityModal, setActiveEditFacilityModal] = useState<boolean>(false);
  const [modalAction, setModalAction] = useState<FormActionType>();
  const { t } = useTranslation();

  const [_isLoading, _error, sendRequest] = useHttp();

  const isPatientOrDoctor = useSelector(selectIsPatientOrDoctor);

  const openModalHandler = (facility: FacilityInterface) => {
    setSelectedFacility(facility);
    setActiveEditFacilityModal(true);
  };

  const deleteHandler = (facilityTemplate: FacilityInterface) => {
    setSelectedFacility(facilityTemplate);

    sendRequest<boolean, FacilityInterface>(
      CoreAPI_URLs.FACILITY_GENERAL_OPERATIONS,
      onFacilityTemplateDeleteHandler,
      "DELETE",
      JSON.stringify(facilityTemplate),
      [{ header: HeaderEnum.RESOURCE_ID, value: facilityTemplate.id }],
      { payload: facilityTemplate, successMessage: t("facilityTable.facilityDeleted") }
    );
  };

  const onFacilityTemplateDeleteHandler = (
    result: boolean,
    facilityTemplate: FacilityInterface
  ) => {
    const indexList = modelList.indexOf(facilityTemplate);
    modelList.splice(indexList, 1);
    setModelList([...modelList]);
  };

  const getUITable = (list: FacilityInterface[]) => {
    const baseColumns = [
      {
        Header: t("facilityTable.name"),
        accessor: "name",
        sortIndicatorType: "az",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("facilityTable.city"),
        accessor: "city",
        sortIndicatorType: "az",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("facilityTable.state"),
        accessor: "state",
        sortIndicatorType: "az",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("facilityTable.zip"),
        accessor: "zip",
        sortIndicatorType: "numeric",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("facilityTable.phone"),
        accessor: "phone",
        sortIndicatorType: "numeric",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("facilityTable.email"),
        accessor: "email",
        sortIndicatorType: "az",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
    ];
    const adminColumns = [
      {
        Header: t("facilityTable.code"),
        accessor: "code",
        sortIndicatorType: "numeric",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("facilityTable.fax"),
        accessor: "fax",
        sortIndicatorType: "numeric",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
    ];

    let finalColumns = [...baseColumns];

    if (!isPatientOrDoctor) {
      finalColumns.splice(1, 0, ...adminColumns);
    }

    const actionsColumn = {
      Header: t("facilityTable.actions"),
      accessor: "id",
      disableSortBy: true,
      sortIndicatorType: "az",
      Cell: ({ value }: any) => (
        <Box display="flex" gap={1}>
          <Button
            name="viewFacilityButton"
            variant="contained"
            color={"info"}
            onClick={() => {
              const facility = list.find((p) => p.id === value);
              if (facility) {
                setModalAction(
                  isPatientOrDoctor ? FormActionType.READ_ONLY : FormActionType.MODIFY
                );
                openModalHandler(facility);
              }
            }}
          >
            {isPatientOrDoctor ? t("facilityTable.viewAction") : t("facilityTable.editAction")}
          </Button>

          <PermissionWrapper
            requiredPermissions={[PermissionsUser[EntityPermissions.FACILITY].DELETE]}
          >
            <ButtonDialog
              name="deleteFacilityButton"
              onAcceptHandle={() => {
                const facility = list.find((p) => p.id === value);
                if (facility) {
                  deleteHandler(facility);
                }
              }}
              buttonText={t("facilityTable.deleteAction")}
              title={t("facilityTable.deleteConfirmation")}
              description={t("facilityTable.description")}
              labelledbyID={"FacilityDelete"}
              describedbyID={"FacilityDescriptionId"}
            />
          </PermissionWrapper>
        </Box>
      ),
    };

    finalColumns.push(actionsColumn);

    return {
      columns: finalColumns,
      rows: list,
    };
  };

  const dataTableData = useMemo(() => getUITable(modelList), [modelList, isPatientOrDoctor]);

  return (
    <>
      <Box mt={2} mb={2} width="100%" display="flex" justifyContent="flex-end">
        <PermissionWrapper
          requiredPermissions={[PermissionsUser[EntityPermissions.FACILITY].CREATE]}
        >
          <Button
            name="newFacilityButton"
            type="button"
            variant="contained"
            color="primary"
            onClick={() => {
              setModalAction(FormActionType.NEW);
              setSelectedFacility(getNewFacilityDataContent());
              setActiveEditFacilityModal(true);
            }}
          >
            {t("facilityForm.newFacility")}
          </Button>
        </PermissionWrapper>
      </Box>

      <DataTable {...props} table={dataTableData} canSearch isLoading={props.isLoading}/>

      <Modal
        open={activeEditFacilityModal}
        onClose={() => {
          setActiveEditFacilityModal(false);
        }}
      >
        <FacilityForm
          modelList={modelList}
          setModelList={setModelList}
          action={modalAction}
          model={selectedFacility}
          onCloseAction={() => {
            setActiveEditFacilityModal(false);
          }}
          isModal={true}
        />
      </Modal>
    </>
  );
};

export default FacilityTable;
